import { textoDaObra } from "@/shared/utils/formatar-identificacao-obra";
import { formatDateBr, OBRA_SITUACAO_CANCELADA } from "@/modules/carga-de-trabalho";
import type {
  AlocacaoDeObra,
  AlocacoesPorLocal,
  ConflitoIgnorado,
  ConflitoLocal,
  LocalComAlocacoes,
  LocalDisponivel,
} from "@/modules/carga-de-trabalho";
import {
  JUSTIFICATIVA_REQUIRED_MESSAGE,
  LOCAL_REQUIRED_MESSAGE,
  OBRA_REQUIRED_MESSAGE,
} from "./resolver-conflito-local.constants";
import type { ResolverConflitoNotice } from "./types-resolver-conflito-local";

/** O core devolve ids em caixas diferentes conforme o endpoint (carga crua, locais em minúsculo). */
export function sameId(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function obraLabel(alocacao: AlocacaoDeObra): string {
  return textoDaObra(alocacao.obra);
}

export function formatPeriodo(inicio: string, fim: string): string {
  return `${formatDateBr(inicio)} - ${formatDateBr(fim)}`;
}

export function conflitoKey(conflito: ConflitoLocal): string {
  return `${conflito.a.id}|${conflito.b.id}|${conflito.inicio}|${conflito.fim}`;
}

/** "Obra A × Obra B · período" — como cada conflito aparece na seleção. */
export function describeConflitoOption(conflito: ConflitoLocal): { title: string; period: string } {
  return {
    title: `${obraLabel(conflito.a)}  ×  ${obraLabel(conflito.b)}`,
    period: formatPeriodo(conflito.inicio, conflito.fim),
  };
}

/** As duas alocações do conflito: cada uma é uma obra que pode ter o local remanejado. */
export function obrasDoConflito(conflito: ConflitoLocal): AlocacaoDeObra[] {
  return [conflito.a, conflito.b].filter(
    (alocacao) => alocacao.obra.situacao !== OBRA_SITUACAO_CANCELADA,
  );
}

export function validateObra(alocacaoId: string | null): string | null {
  return alocacaoId ? null : OBRA_REQUIRED_MESSAGE;
}

export function validateLocal(localId: string | null): string | null {
  return localId ? null : LOCAL_REQUIRED_MESSAGE;
}

export function validateJustificativa(value: string): string | null {
  return value.trim() === "" ? JUSTIFICATIVA_REQUIRED_MESSAGE : null;
}

export interface RevisaoInput {
  locais: LocalComAlocacoes[];
  ignorados: ConflitoIgnorado[];
  origem: { id: string; nome: string };
  alocacao: AlocacaoDeObra;
  substituto: Pick<LocalDisponivel, "id" | "codigo" | "nome">;
}

export const TRANSFERIDA_SUFFIX = "-transferida";

/**
 * Dados do Gantt de revisão: o Local de origem como está hoje e o substituto com as
 * alocações que já tem mais a obra transferida (id com sufixo, desenhada tracejada).
 */
export function buildRevisaoData({
  locais,
  ignorados,
  origem,
  alocacao,
  substituto,
}: RevisaoInput): AlocacoesPorLocal {
  const origemAtual = locais.find((local) => sameId(local.id, origem.id));
  const substitutoAtual = locais.find((local) => sameId(local.id, substituto.id));

  return {
    locais: [
      {
        id: origem.id,
        codigo: origemAtual?.codigo ?? "",
        nome: origem.nome,
        alocacoes: origemAtual?.alocacoes ?? [alocacao],
      },
      {
        id: substituto.id,
        codigo: substituto.codigo,
        nome: substituto.nome,
        alocacoes: [
          ...(substitutoAtual?.alocacoes ?? []),
          { ...alocacao, id: `${alocacao.id}${TRANSFERIDA_SUFFIX}` },
        ],
      },
    ],
    semLocal: [],
    conflitosIgnorados: ignorados,
  };
}

/** `N dias corridos` do trecho em conflito (intervalo fechado). */
export function diasCorridos(inicio: string, fim: string): number {
  const toUtc = (value: string) => {
    const [y = 1970, m = 1, d = 1] = value.slice(0, 10).split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.max(Math.round((toUtc(fim) - toUtc(inicio)) / 86_400_000) + 1, 1);
}

/** `DD/MM - DD/MM (N dias corridos)`, como no protótipo. */
export function descreverSobreposicao(conflito: ConflitoLocal): string {
  const dias = diasCorridos(conflito.inicio, conflito.fim);
  const short = (value: string) => formatDateBr(value).slice(0, 5);
  return `${short(conflito.inicio)} - ${short(conflito.fim)} (${dias} ${
    dias === 1 ? "dia corrido" : "dias corridos"
  })`;
}

export function remanejadoNotice(
  alocacao: AlocacaoDeObra,
  substituto: Pick<LocalDisponivel, "nome">,
  novoConflito: boolean,
): ResolverConflitoNotice {
  if (novoConflito) {
    return {
      severity: "warn",
      summary: `Local remanejado, mas há um novo conflito em ${substituto.nome}. Revise o cronograma.`,
    };
  }

  return {
    severity: "success",
    summary: `Local remanejado. O conflito da Obra ${alocacao.obra.codigo} foi resolvido.`,
  };
}
